// Category-appropriate images via loremflickr (Flickr's CC pool).
// URL format: https://loremflickr.com/800/600/<tags>/all?lock=<seed>
//
// Key rules:
//   1. Prefer SHORT, HIGH-SIGNAL tags. Multi-tag with vague nouns (e.g.
//      "kitchen,cookware,homewares") tends to return showroom / store-interior
//      photos rather than the product itself. Single strong nouns work best.
//   2. For categories whose products are highly heterogeneous (Household,
//      Groceries) we derive per-product tags from the title so each product
//      gets a photo that actually matches the item.
//   3. To guarantee different photos across products in the same category we
//      combine hash(product.id) with hash(product.title) into the lock seed.

// Broad default tags per category (used unless a per-product override applies)
export const CATEGORY_TAGS = {
  cars: "supercar",
  houses: "luxury-home",
  boats: "yacht",
  flights: "airplane",
  hotels: "resort",
  electronics: "gadget",
  "home-kitchen": "cookware",
  clothing: "outfit",
  beauty: "cosmetics",
  toys: "toy",
  sports: "gym-equipment",
  books: "book",
  groceries: "grocery",
  pet: "puppy",
  office: "desk",
  household: "cleaning",
  specials: "beverage-can",
};

// Per-product tag overrides driven by keywords in the product title.
// The FIRST matching entry wins — put MORE SPECIFIC / MULTI-WORD keywords
// before shorter ones (e.g. "popcorn" before "sea salt", "olive oil" before "oil").
const KEYWORD_TAGS = [
  // ---- Household Essentials (varied bulk items) ----
  { match: ["toilet paper"], tag: "toilet-paper" },
  { match: ["paper towels", "paper towel"], tag: "paper-towel-roll" },
  { match: ["milk"], tag: "milk-jug" },
  { match: ["bread", "sourdough"], tag: "bread-loaf" },
  { match: ["bottled spring water", "bottled water"], tag: "water-bottle" },
  { match: ["aluminum foil"], tag: "aluminum-foil" },
  { match: ["ziplock", "freezer bags"], tag: "ziplock-bag" },
  { match: ["trash bag"], tag: "trash-bag" },
  { match: ["dish soap"], tag: "dish-soap" },
  { match: ["laundry detergent"], tag: "laundry-detergent" },
  { match: ["hand soap"], tag: "hand-soap-pump" },
  { match: ["sanitizing wipes", "wipes"], tag: "wipes-canister" },
  { match: ["aa alkaline", "aaa alkaline", "batteries"], tag: "battery-pack" },
  { match: ["led light bulb", "light bulb"], tag: "light-bulb" },
  { match: ["paper plates"], tag: "paper-plate" },
  { match: ["paper cups"], tag: "paper-cup" },
  { match: ["plastic cutlery", "compostable cutlery"], tag: "plastic-utensils" },
  { match: ["napkins"], tag: "napkin" },
  { match: ["coffee filters"], tag: "coffee-filter" },
  { match: ["cotton swabs"], tag: "cotton-swab" },
  { match: ["cotton balls"], tag: "cotton-ball" },
  { match: ["toothpaste"], tag: "toothpaste-tube" },
  { match: ["toothbrush"], tag: "toothbrush" },
  { match: ["shampoo"], tag: "shampoo-bottle" },
  { match: ["conditioner"], tag: "conditioner-bottle" },
  { match: ["deodorant"], tag: "deodorant-stick" },
  { match: ["facial tissue", "tissues"], tag: "tissue-box" },
  { match: ["feminine care"], tag: "sanitary-pad" },
  { match: ["baby diapers", "diaper"], tag: "baby-diapers" },
  { match: ["rice", "basmati"], tag: "rice-bag" },
  { match: ["flour"], tag: "flour-sack" },
  { match: ["sugar"], tag: "sugar-sack" },
  // ---- Groceries (VARIED — put multi-word / distinctive keywords FIRST) ----
  { match: ["popcorn"], tag: "popcorn" },  // must precede "sea salt"
  { match: ["dark chocolate", "chocolate bar"], tag: "chocolate-bar" },
  { match: ["olive oil"], tag: "olive-oil-bottle" },
  { match: ["balsamic vinegar"], tag: "vinegar-bottle" },
  { match: ["sea salt flakes", "sea salt"], tag: "salt" },
  { match: ["rolled oats", "oatmeal"], tag: "oats" },
  { match: ["almond butter"], tag: "almond-butter" },
  { match: ["wildflower honey", "honey"], tag: "honey-jar" },
  { match: ["artisan pasta", "rigatoni", "pasta"], tag: "pasta" },
  { match: ["basmati rice"], tag: "rice" },
  { match: ["cashews"], tag: "cashews" },
  { match: ["kombucha"], tag: "kombucha" },
  { match: ["oat milk"], tag: "oat-milk" },
  { match: ["almond flour"], tag: "almond-flour" },
  { match: ["cinnamon"], tag: "cinnamon" },
  { match: ["green tea", "tea bag"], tag: "tea" },
  { match: ["sparkling water"], tag: "sparkling-water" },
  { match: ["whole bean coffee", "coffee"], tag: "coffee-beans" },
  // ---- Home & Kitchen (heterogeneous — needs explicit mappings) ----
  { match: ["french press"], tag: "french-press" },
  { match: ["espresso maker", "espresso machine"], tag: "espresso-machine" },
  { match: ["stand mixer"], tag: "stand-mixer" },
  { match: ["immersion blender"], tag: "immersion-blender" },
  { match: ["air fryer"], tag: "air-fryer" },
  { match: ["cast iron skillet", "skillet"], tag: "skillet" },
  { match: ["non-stick frying pan", "frying pan"], tag: "frying-pan" },
  { match: ["stainless cookware", "cookware set"], tag: "cookware-set" },
  { match: ["memory foam pillow", "pillow"], tag: "pillow" },
  { match: ["cotton duvet", "duvet"], tag: "duvet-cover" },
  { match: ["bath towel"], tag: "bath-towel" },
  { match: ["ceramic dinner set", "dinner set"], tag: "dinnerware" },
  { match: ["cutting board"], tag: "cutting-board" },
  { match: ["aromatherapy candle", "candle"], tag: "scented-candle" },
  { match: ["floor lamp"], tag: "floor-lamp" },
  { match: ["cordless vacuum"], tag: "vacuum-cleaner" },
  { match: ["weighted blanket"], tag: "weighted-blanket" },
  // ---- Some Electronics disambiguation ----
  { match: ["earbuds"], tag: "earbuds" },
  { match: ["headphones"], tag: "headphones" },
  { match: ["smart tv", "oled"], tag: "tv" },
  { match: ["smartphone"], tag: "smartphone" },
  { match: ["laptop"], tag: "laptop" },
  { match: ["tablet"], tag: "tablet" },
  { match: ["smartwatch"], tag: "smartwatch" },
  { match: ["bluetooth speaker"], tag: "speaker" },
  { match: ["gaming console"], tag: "game-console" },
  { match: ["mirrorless camera", "camera"], tag: "camera" },
  { match: ["mechanical keyboard"], tag: "keyboard" },
  { match: ["wireless mouse"], tag: "computer-mouse" },
  { match: ["robot vacuum"], tag: "robot-vacuum" },
  { match: ["air purifier"], tag: "air-purifier" },
  { match: ["projector"], tag: "projector" },
];

function tagForProduct(product) {
  const title = String(product?.title || "").toLowerCase();
  for (const rule of KEYWORD_TAGS) {
    if (rule.match.some((k) => title.includes(k))) return rule.tag;
  }
  return CATEGORY_TAGS[product?.category] || "product";
}

// Deterministic 31-bit FNV-1a hash of a string → non-negative integer.
export function hashSeed(str) {
  let h = 2166136261 >>> 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

function _isExplicitImage(url) {
  if (!url) return false;
  return url.startsWith("/") || url.startsWith("data:") || url.includes("customer-assets");
}

/**
 * Category-appropriate image URL for a product.
 * Uses per-product keyword tags first for accurate matches, category tag as
 * fallback. Combines hash(id) + hash(title) so different products always get
 * different photos even if their tag is the same.
 */
export function getProductImage(product, variant = 0) {
  if (!product) return "";
  if (Array.isArray(product.images) && product.images.length > 0) {
    return product.images[variant % product.images.length];
  }
  if (_isExplicitImage(product.image)) {
    return product.image;
  }
  const tag = tagForProduct(product);
  const seed = (hashSeed(product.id) ^ hashSeed(product.title || "")) >>> 0;
  const lock = ((seed + variant * 1013904223) >>> 0) % 100000;
  return `https://loremflickr.com/800/600/${tag}/all?lock=${lock}`;
}

export function getProductImages(product) {
  if (Array.isArray(product?.images) && product.images.length > 0) {
    return product.images;
  }
  return [0, 1, 2, 3].map((i) => getProductImage(product, i));
}
